import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  api,
  ApiError,
  setCsrfToken,
  setUnauthorizedHandler,
  toApiError,
  unwrap,
  type Me,
} from "../api/client";

type AuthStatus = "loading" | "authenticated" | "anonymous" | "error";

export interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  error: ApiError | null;
  /** True after the user logged out on purpose (login should not offer to "continue"). */
  loggedOut: boolean;
  /** Re-read `/api/auth/me` (e.g. after accepting an invitation). */
  refresh: () => Promise<Me | null>;
  /** Throws ApiError (401 wrong credentials, 429 rate limited). */
  login: (email: string, password: string) => Promise<Me>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [error, setError] = useState<ApiError | null>(null);
  const [loggedOut, setLoggedOut] = useState(false);

  const accept = useCallback((next: Me | null) => {
    setMe(next);
    setCsrfToken(next?.csrf_token ?? null);
    setStatus(next ? "authenticated" : "anonymous");
    setError(null);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const next = await unwrap(api.GET("/api/auth/me"));
      accept(next);
      return next;
    } catch (err) {
      const e = toApiError(err);
      if (e.status === 401) {
        accept(null);
      } else {
        setError(e);
        setStatus("error");
      }
      return null;
    }
  }, [accept]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    setUnauthorizedHandler(() => accept(null));
    return () => setUnauthorizedHandler(null);
  }, [accept]);

  const login = useCallback(
    async (email: string, password: string) => {
      const next = await unwrap(api.POST("/api/auth/login", { body: { email, password } }));
      setLoggedOut(false);
      accept(next);
      return next;
    },
    [accept],
  );

  const logout = useCallback(async () => {
    try {
      await unwrap(api.POST("/api/auth/logout"));
    } catch {
      // The session is gone either way; clear local state.
    }
    setLoggedOut(true);
    accept(null);
  }, [accept]);

  const value = useMemo(
    () => ({ status, me, error, loggedOut, refresh, login, logout }),
    [status, me, error, loggedOut, refresh, login, logout],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}

/** The signed-in user (only call below <RequireAuth>). */
export function useMe(): Me {
  const { me } = useAuth();
  if (!me) throw new Error("useMe called without an authenticated user");
  return me;
}
